import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { SiteHeader } from '@/components/SiteHeader';
import axios from 'axios';
import { ArrowRight, Calendar, Tag, ChevronLeft, ChevronRight } from 'lucide-react';

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog?page=${currentPage}&limit=10`)
      .then(r => {
        setPosts(r.data.posts || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.pages || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentPage]);

  const goToPage = (page) => {
    setSearchParams({ page: page.toString() });
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-page">
      <SiteHeader />

      <main className="max-w-4xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-12">
          <span className="text-xs text-muted-foreground uppercase tracking-widest">Blog</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mt-2 mb-3" data-testid="blog-title">
            Insights & Updates
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Tips on AI chatbots, customer support automation, and growing your business with Whisper.AI.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground text-sm">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl">
            <p className="text-sm text-muted-foreground">No blog posts yet. Check back soon!</p>
          </div>
        ) : (
          <>
            <div className="space-y-6" data-testid="blog-list">
              {posts.map((post) => (
                <Link key={post.post_id} to={`/blog/${post.slug}`} className="block group" data-testid={`blog-card-${post.slug}`}>
                  <article className="p-6 rounded-xl border border-border bg-card hover:border-[#10B981]/30 transition-all">
                    <div className="flex gap-6">
                      {post.cover_image && (
                        <div className="hidden sm:block w-48 h-32 rounded-lg overflow-hidden shrink-0 bg-muted">
                          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                          {post.tags?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Tag className="h-3 w-3" />
                              {post.tags.slice(0, 3).join(', ')}
                            </span>
                          )}
                        </div>
                        <h2 className="text-lg font-semibold text-foreground group-hover:text-[#10B981] transition-colors mb-2">{post.title}</h2>
                        {post.excerpt && (
                          <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">{post.excerpt}</p>
                        )}
                        <span className="inline-flex items-center gap-1 text-xs text-[#10B981] font-medium mt-3 group-hover:gap-2 transition-all">
                          Read more <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10" data-testid="blog-pagination">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => goToPage(currentPage - 1)}
                  className="gap-1"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 text-xs text-muted-foreground">...</span>
                    ) : (
                      <Button
                        key={p}
                        variant={p === currentPage ? 'default' : 'outline'}
                        size="sm"
                        className={`w-9 h-9 ${p === currentPage ? 'bg-[#10B981] hover:bg-[#059669] text-white' : ''}`}
                        onClick={() => goToPage(p)}
                        data-testid={`page-${p}-btn`}
                      >
                        {p}
                      </Button>
                    )
                  )}

                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => goToPage(currentPage + 1)}
                  className="gap-1"
                  data-testid="next-page-btn"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            <p className="text-center text-xs text-muted-foreground mt-4">
              Showing {((currentPage - 1) * 10) + 1}–{Math.min(currentPage * 10, total)} of {total} posts
            </p>
          </>
        )}
      </main>

      <footer className="border-t border-border py-6 px-6 sm:px-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Whisper.AI &mdash; Powered by WPO</span>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">Home</Link>
        </div>
      </footer>
    </div>
  );
}
